/**
 * arrowGeometry — pure 2D arrowhead / tick polygon construction for the PDF
 * exporter (PDF-5b DIMENSION / MULTILEADER arrowheads).
 *
 * The scene-side arrowhead prototypes (`scene3d/dimensions/arrowMarkerMesh.ts`)
 * are `THREE.BufferGeometry` at unit size — unusable on the pure PDF backend.
 * This module reproduces the SAME documented unit-cell vertex math (tip at the
 * origin, body along the negative-X axis, ±0.5 half-width; dot centred at
 * (-0.5,0) r=0.5; arch / oblique slashes) as RAW world `Point2` polygons, then
 * places each through the per-instance transform the prototype header
 * specifies: scale by `size`, rotate by `atan2(dir.y, dir.x)`, translate by
 * `tip`. No simplification (CLAUDE.md PRIORITY #1) — identical proportions to
 * the renderer's arrow blocks.
 */

import type { ArrowVariant } from "../../../scene3d/types";
import type { Point2 } from "../cadPrimitive";

const HALF_WIDTH = 0.5;

interface Vec2 {
  x: number;
  y: number;
}

/**
 * Place a list of unit-cell points (x along the arrow axis, y perpendicular)
 * into world space: scale by `size`, rotate so −X maps onto −`dir`, translate
 * by `tip`. `dir` is the direction the arrow POINTS (unit vector); the body
 * lies behind the tip along −dir.
 */
function placeUnitCell(
  unit: ReadonlyArray<Vec2>,
  tip: Point2,
  dir: Vec2,
  size: number,
): Point2[] {
  const len = Math.hypot(dir.x, dir.y) || 1;
  const cos = dir.x / len;
  const sin = dir.y / len;
  return unit.map((u) => {
    const sx = u.x * size;
    const sy = u.y * size;
    // Rotate (sx,sy) by the arrow angle, then translate to the tip.
    return {
      x: tip.x + sx * cos - sy * sin,
      y: tip.y + sx * sin + sy * cos,
    };
  });
}

/**
 * Whether the arrow variant is a SOLID (filled) arrowhead. Matches the renderer
 * `getArrowPrototype().fill` flag: closed-filled / closed / dot are filled; the
 * open / blank variants are outline-only; none draws nothing.
 */
export function isFilledArrow(variant: ArrowVariant): boolean {
  switch (variant) {
    case "closed-filled":
    case "closed":
    case "dot":
      return true;
    default:
      return false;
  }
}

/**
 * The closed FILL-ONLY polygon for a solid arrowhead. Returns the world-space
 * vertices of the closed loop (the caller adds the implicit closing edge).
 *
 *   - closed-filled / closed → triangle: tip (0,0), base (-1, +0.5), (-1, -0.5).
 *   - dot                    → 16-gon approximating the filled circle centred
 *                              at (-0.5,0), r=0.5 (same footprint as the arrow).
 *
 * For non-filled variants returns an empty array.
 */
export function arrowFillPolygon(
  tip: Point2,
  dir: Vec2,
  size: number,
  variant: ArrowVariant,
): Point2[] {
  if (variant === "closed-filled" || variant === "closed") {
    return placeUnitCell(
      [
        { x: 0, y: 0 },
        { x: -1, y: HALF_WIDTH },
        { x: -1, y: -HALF_WIDTH },
      ],
      tip,
      dir,
      size,
    );
  }
  if (variant === "dot") {
    const segments = 16;
    const cx = -0.5;
    const r = 0.5;
    const unit: Vec2[] = [];
    for (let i = 0; i < segments; i++) {
      const a = (i / segments) * Math.PI * 2;
      unit.push({ x: cx + r * Math.cos(a), y: r * Math.sin(a) });
    }
    return placeUnitCell(unit, tip, dir, size);
  }
  return [];
}

/**
 * The stroked outline polyline for an open / blank arrowhead.
 *
 *   - closed-blank → outlined triangle (tip → top → bottom → tip).
 *   - open / open30 → open V (top corner → tip → bottom corner), no closing
 *     edge (~26.5° / ~15° half angle, matching the renderer).
 *   - dot-blank → outlined circle (16-gon, closed back to start).
 *
 * For filled / none variants returns an empty array (caller should not stroke).
 */
export function arrowOutlinePolyline(
  tip: Point2,
  dir: Vec2,
  size: number,
  variant: ArrowVariant,
): Point2[] {
  if (variant === "closed-blank") {
    return placeUnitCell(
      [
        { x: 0, y: 0 },
        { x: -1, y: HALF_WIDTH },
        { x: -1, y: -HALF_WIDTH },
        { x: 0, y: 0 },
      ],
      tip,
      dir,
      size,
    );
  }
  if (variant === "open" || variant === "open30") {
    const dy = variant === "open30" ? Math.tan((15 * Math.PI) / 180) : 0.5;
    return placeUnitCell(
      [
        { x: -1, y: dy },
        { x: 0, y: 0 },
        { x: -1, y: -dy },
      ],
      tip,
      dir,
      size,
    );
  }
  if (variant === "dot-blank") {
    const segments = 16;
    const cx = -0.5;
    const r = 0.5;
    const unit: Vec2[] = [];
    for (let i = 0; i <= segments; i++) {
      const a = (i / segments) * Math.PI * 2;
      unit.push({ x: cx + r * Math.cos(a), y: r * Math.sin(a) });
    }
    return placeUnitCell(unit, tip, dir, size);
  }
  return [];
}

/**
 * The stroked slash polyline for a tick primitive.
 *
 *   - arch-tick → 45° slash from back-side (-0.45,-0.45) to tip-side
 *     (0.45,0.45) of the unit cell (the diagonal slash AutoCAD draws).
 *   - oblique   → vertical slash from (0,-0.5) to (0,+0.5).
 *
 * Drawn as a single line segment (stroked), scaled by `size`. The
 * filled-strip thickness the mesh prototype uses is a render detail; on the
 * plot the tick is a stroked line of the dimension's pen width (AutoCAD parity).
 */
export function tickPolyline(
  tip: Point2,
  dir: Vec2,
  size: number,
  variant: "arch-tick" | "oblique",
): Point2[] {
  if (variant === "oblique") {
    return placeUnitCell(
      [
        { x: 0, y: -0.5 },
        { x: 0, y: 0.5 },
      ],
      tip,
      dir,
      size,
    );
  }
  // arch-tick
  return placeUnitCell(
    [
      { x: -0.45, y: -0.45 },
      { x: 0.45, y: 0.45 },
    ],
    tip,
    dir,
    size,
  );
}
